import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { deleteAttributeValue, fetchAttributeValuesListView } from "../../shared/catalogMastersApi";
import { IconPlus } from "../../shared/toolbarIcons";
import { mastersDataGridSx } from "../../shared/mastersDataGridSx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

export function AttributeValuesList() {
  const navigate = useNavigate();
  const [values, setValues] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const result = await fetchAttributeValuesListView();
    if (!result.ok) {
      setError(result.error ?? "Failed to load data");
      setValues([]);
      setAttributes([]);
    } else {
      setValues(result.values);
      setAttributes(result.attributes);
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const onEdit = (x) => {
    navigate(`/attributes?edit=${x.attributeId}&tab=values&valueEdit=${x.id}`);
  };

  const onDelete = async (id) => {
    if (!(await window.confirmDialog("Delete this attribute value?"))) return;
    const res = await deleteAttributeValue(id);
    if (!res.ok) setError(res.error ?? "Failed to delete attribute value");
    else await load();
  };

  const attributeById = useMemo(() => {
    const map = new Map();
    for (const attribute of attributes) map.set(attribute.id, attribute);
    return map;
  }, [attributes]);

  const rows = useMemo(
    () =>
      values.map((value) => ({
        id: value.id,
        item: value,
        attributeName: attributeById.get(value.attributeId)?.name ?? `— (#${value.attributeId})`,
        valueText: value.value ?? "—",
        colorCode: value.colorCode ?? "—",
        order: value.order ?? 0
      })),
    [values, attributeById]
  );

  const total = rows.length;
  const columns = [
      { field: "attributeName", headerName: "Attribute", flex: 1, minWidth: 150 },
      { field: "valueText", headerName: "Value", flex: 1, minWidth: 150 },
      { field: "colorCode", headerName: "Color", flex: 0.8, minWidth: 120 },
      { field: "order", headerName: "Order", width: 100 },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <div className="table-actions">
            <button className="icon-button" type="button" title="Edit" onClick={() => onEdit(params.row.item)}>
              ✏️
            </button>
            <button className="icon-button danger" type="button" title="Delete" onClick={() => void onDelete(params.row.item.id)}>
              🗑️
            </button>
          </div>
        )
      }
  ];

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Attribute values</h2>
          <p>{loading ? "Loading attribute values..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <Link to="/attributes" className="icon-button icon-button--toolbar" title="Add attribute" aria-label="Add attribute">
            <IconPlus />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={mastersDataGridSx}
          />
        </div>
      </div>
    </div>
  );
}
